import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { UIRouter } from '@uirouter/angular';
import { MonLoadingService } from '@monsido/services/loading/services/loading.service';


export type Theme = 'legacy' | 'modern';

@Injectable({
    providedIn: 'root',
})
export class ThemeService {
    private legacyStylesheet: HTMLLinkElement | null = null;
    private modernStylesheet: HTMLLinkElement | null = null;
    private legacyTopMenuStylesheet: HTMLLinkElement | null = null;
    private currentTheme?: 'legacy' | 'modern';
    private stylesheetLoadingPromise: Promise<void> | null = null;
    private loader: { close(): void } | null = null;

    constructor (@Inject(DOCUMENT) readonly document: Document, readonly uiRouter: UIRouter,
        readonly loadingService: MonLoadingService) {
        this.initializeThemeFromRoute();
        this.listenToUIRouterTransitions();
    }

    init () {
        this.loadModernTheme().then();
    }

    public getStylesheetLoadingPromise (): Promise<void> | null {
        return this.stylesheetLoadingPromise;
    }

    public getCurrentTheme (): Theme {
        return this.currentTheme ?? 'legacy';
    }

    private setTheme (theme: Theme): void {
        this.applyThemeMarker(theme);

        if (this.currentTheme === theme) {
            return;
        }

        const previousTheme = this.currentTheme;
        this.currentTheme = theme;
        this.updateStylesheets(previousTheme !== undefined);
    }

    private listenToUIRouterTransitions (): void {
        this.uiRouter.transitionService.onBefore({}, async (transition) => {
            // Check for explicit theme declared in state data (used by lazy-loaded modules)
            const stateData = transition.$to().data;
            if (stateData?.theme) {
                this.setTheme(stateData.theme);
                return true;
            }

            // Fall back to state-name-based detection for other modules (e.g. admin)
            const toState = transition.to();
            const stateName = toState.name || '';
            const theme = this.getThemeFromUrl(stateName);
            this.setTheme(theme);
            return true;
        });

        this.uiRouter.transitionService.onFinish({}, async () => {
            if (this.stylesheetLoadingPromise) {
                await this.stylesheetLoadingPromise;
            }
            if (this.loader) {
                this.loader.close();
            }
            this.loader = null;
            return true;
        });

        this.uiRouter.transitionService.onError({}, async () => {
            if (this.loader) {
                this.loader.close();
            }
            this.loader = null;
        });
    }

    private initializeThemeFromRoute (): void {
        const currentUrl = window.location.pathname;
        const theme = this.getThemeFromUrl(currentUrl);
        this.setTheme(theme);
    }

    private getThemeFromUrl (url: string): 'legacy' | 'modern' {
        if ((url.includes('admin') && !url.includes('admin_old')) ||
            url.includes('accessibility') ||
            url.includes('notifications') ||
            url.includes('edit-profile')) {
            return 'modern';
        }
        return 'legacy';
    }

    private applyThemeMarker (theme: Theme): void {
        this.document.documentElement.setAttribute('data-ui-theme', theme);
        this.document.body.setAttribute('data-ui-theme', theme);
        this.document.body.setAttribute('data-ui-shell', theme);
    }

    private updateStylesheets (showLoader: boolean = false): void {
        this.loader = showLoader ? this.loadingService.show() : null;

        this.stylesheetLoadingPromise = this.withTimeout(Promise.resolve().then(async () => {
            if (this.currentTheme === 'modern') {
                await this.loadModernTheme();
                this.removeStylesheet(this.legacyStylesheet);
            } else {
                await this.loadLegacyTheme();
            }

            try {
                await this.withTimeout(this.document.fonts.ready, 10_000);
            } catch {}
            this.stylesheetLoadingPromise = null;
        }).catch(() => {
            this.stylesheetLoadingPromise = null;
            if (this.loader) {
                this.loader.close();
            }
        }), 15_000);
    }

    private createStylesheet (href: string): Promise<HTMLLinkElement> {
        return new Promise((resolve, reject) => {
            const link = this.document.createElement('link');
            link.rel = 'stylesheet';
            link.href = href;
            link.onload = () => resolve(link);
            link.onerror = () => reject(new Error(`Failed to load stylesheet: ${href}`));
            this.document.head.appendChild(link);
        });
    }

    private removeStylesheet (stylesheet: HTMLLinkElement | null): void {
        if (stylesheet?.parentNode) {
            stylesheet.parentNode.removeChild(stylesheet);
        }
    }

    private async loadLegacyTheme (): Promise<void> {
        this.legacyStylesheet = await this.createStylesheet('/legacy-theme.css');
    }

    private withTimeout<T> (promise: Promise<T>, duration: number): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            const timer = setTimeout(() => reject(new Error(`Timed out after ${duration}ms`)), duration);
            promise.then(
                (value) => {
                    clearTimeout(timer); resolve(value);
                },
                (error) => {
                    clearTimeout(timer); resolve(error);
                },
            );
        });
    }

    private async loadModernTheme (): Promise<void> {
        this.document.documentElement.style.fontSize = '16px';
        await this.createStylesheet('/modern-theme.css');
    }
}
